import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import {
  getUserBellNotifications,
  markNotificationAsRead,
  NOTIFICATION_TYPES,
} from '@/lib/adminNotifications';
import { collection, query, where, orderBy, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ysr2Mobile } from '@/features/shared/styles/mobileDesignSystem';
import { 
  Bell, 
  BellOff, 
  MessageSquare, 
  AlertCircle, 
  Megaphone, 
  Info, 
  Gift,
  X,
  Check,
  RefreshCw,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

const UserNotificationCenter = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const mobile = useMobile();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [expandedNotificationId, setExpandedNotificationId] = useState(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Set up real-time notification listener
  useEffect(() => {
    console.log('🔍 UserNotificationCenter: useEffect triggered', { user: !!user, userUid: user?.uid });
    if (!user) return;
    
    let cleanup = null;
    
    // Set up listener asynchronously
    setupNotificationListener().then((cleanupFn) => {
      cleanup = cleanupFn;
    }).catch((error) => {
      console.error('❌ Error setting up notification listener:', error);
    });
    
    // Cleanup listener on unmount
    return () => {
      if (cleanup) {
        console.log('🔔 Cleaning up notification listener');
        cleanup();
      }
    };
  }, [user]);

  /**
   * Opening the bell counts as viewing notifications: create read receipts for all currently-unread
   * items so Admin Console read tallies update (via onNotificationReadCreated → readCount).
   */
  useEffect(() => {
    if (!showNotifications || !user) return undefined;

    let cancelled = false;

    const markUnreadAsReadOnOpen = async () => {
      try {
        const list = await getUserBellNotifications(user.uid);
        const unread = list.filter((n) => !n.read);
        if (unread.length === 0) {
          if (!cancelled) setNotifications(list);
          return;
        }
        await Promise.all(unread.map((n) => markNotificationAsRead(n.id, user.uid)));
        const next = await getUserBellNotifications(user.uid);
        if (!cancelled) setNotifications(next);
      } catch (e) {
        console.error('🔔 Mark-as-read on notification panel open failed:', e);
      }
    };

    markUnreadAsReadOnOpen();

    return () => {
      cancelled = true;
    };
  }, [showNotifications, user]);

  const setupNotificationListener = async () => {
    console.log('🔔 Setting up real-time notification listeners for user:', user.uid);

    loadNotifications();

    const refreshInterval = setInterval(() => {
      loadNotifications();
    }, 20000);

    const unsubs = [];

    const safeOnSnapshot = (q, label) => {
      try {
        const unsub = onSnapshot(
          q,
          () => {
            console.log(`🔔 admin_notifications snapshot (${label}) → refresh`);
            loadNotifications();
          },
          (error) => {
            if (error.code !== 'failed-precondition') {
              console.warn(`⚠️ Notification listener (${label}):`, error.message);
            }
          }
        );
        unsubs.push(unsub);
      } catch (e) {
        console.warn(`⚠️ Could not subscribe (${label}):`, e?.message);
      }
    };

    safeOnSnapshot(
      query(
        collection(db, 'admin_notifications'),
        where('targetUserId', '==', user.uid),
        orderBy('createdAt', 'desc')
      ),
      'targetUserId'
    );

    safeOnSnapshot(
      query(
        collection(db, 'admin_notifications'),
        where('targetUserIds', 'array-contains', user.uid),
        orderBy('createdAt', 'desc')
      ),
      'targetUserIds'
    );

    safeOnSnapshot(
      query(
        collection(db, 'admin_notifications'),
        where('targetAudience', '==', 'all'),
        orderBy('createdAt', 'desc')
      ),
      'all'
    );

    let accountType = 'artist';
    try {
      const prof = await getDoc(doc(db, 'profiles', user.uid));
      if (prof.exists()) {
        accountType = prof.data()?.account_type || 'artist';
      }
    } catch (e) {
      console.warn('⚠️ Could not load profile for notification listeners:', e?.message);
    }

    if (accountType === 'artist') {
      safeOnSnapshot(
        query(
          collection(db, 'admin_notifications'),
          where('targetAudience', '==', 'artists'),
          orderBy('createdAt', 'desc')
        ),
        'artists'
      );
    } else if (accountType === 'karaoke') {
      safeOnSnapshot(
        query(
          collection(db, 'admin_notifications'),
          where('targetAudience', '==', 'karaoke'),
          orderBy('createdAt', 'desc')
        ),
        'karaoke'
      );
    }

    return () => {
      clearInterval(refreshInterval);
      unsubs.forEach((u) => {
        try {
          u();
        } catch (_) {
          /* noop */
        }
      });
    };
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      console.log('🔍 UserNotificationCenter: Loading notifications for user:', user.uid);
      const list = await getUserBellNotifications(user.uid);
      console.log('🔍 UserNotificationCenter: Received notifications:', list.length, 'unread:', list.filter((n) => !n.read).length);
      setNotifications(list);
    } catch (error) {
      console.error('Error loading notifications:', error);
      console.error('Error details:', error.code, error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    setExpandedNotificationId((prev) => (prev === notification.id ? null : notification.id));

    if (!notification.read) {
      try {
        const ok = await markNotificationAsRead(notification.id, user.uid);
        if (ok) {
          setNotifications((prev) =>
            prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
          );
        }
      } catch (error) {
        console.error('Error marking notification as read:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Could not mark notification as read.',
        });
      }
    }

    if (notification.type === NOTIFICATION_TYPES.SUPPORT_RESPONSE) {
      setShowNotifications(false);
      window.dispatchEvent(
        new CustomEvent('openMelody', {
          detail: { reason: 'support_response' },
          bubbles: true,
        })
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return;
    try {
      await Promise.all(unread.map((n) => markNotificationAsRead(n.id, user.uid)));
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setExpandedNotificationId(null);
      toast({
        title: 'All caught up',
        description: 'Notifications marked as read. Your history is still available below.',
      });
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to mark all as read',
      });
    }
  };

  const getNotificationTypeIcon = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.ANNOUNCEMENT:
        return <Megaphone className="h-4 w-4 text-blue-600" />;
      case NOTIFICATION_TYPES.UPDATE:
        return <Info className="h-4 w-4 text-green-600" />;
      case NOTIFICATION_TYPES.WARNING:
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case NOTIFICATION_TYPES.PROMOTION:
        return <Gift className="h-4 w-4 text-purple-600" />;
      case NOTIFICATION_TYPES.SUPPORT_RESPONSE:
        return <MessageSquare className="h-4 w-4 text-blue-600" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date.toDate()).toLocaleString();
  };

  if (!user) return null;

  const renderBellNotificationRow = (notification, { dark } = { dark: false }) => {
    const expanded = expandedNotificationId === notification.id;
    const msgMuted = dark ? 'text-slate-300' : 'text-gray-600';
    const titleCls = dark
      ? `font-medium text-sm ${notification.read ? 'text-slate-200' : 'text-white'}`
      : `font-medium text-sm ${notification.read ? 'text-gray-700' : 'text-gray-900'}`;

    return (
      <div
        role="button"
        tabIndex={0}
        className={`p-4 transition-colors cursor-pointer ${
          dark
            ? `hover:bg-blue-500/10 ${!notification.read ? 'bg-purple-900/15 border-l-2 border-l-purple-400/80' : ''}`
            : `hover:bg-gray-50 ${!notification.read ? 'bg-blue-50/80 border-l-2 border-l-blue-500/70' : ''}`
        }`}
        onClick={() => handleNotificationClick(notification)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleNotificationClick(notification);
          }
        }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">{getNotificationTypeIcon(notification.type)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className={`${titleCls} ${expanded ? '' : 'truncate'}`}>{notification.title}</h4>
              {expanded ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0 opacity-60 mt-0.5" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-60 mt-0.5" aria-hidden />
              )}
            </div>
            <p
              className={`text-sm mb-2 whitespace-pre-wrap break-words ${msgMuted} ${
                expanded ? '' : 'line-clamp-2'
              }`}
            >
              {notification.message}
            </p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-500'}`}>
                {formatDate(notification.createdAt)}
              </span>
              {!notification.read && (
                <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
                  New
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative flex items-center gap-2"
      >
        <Bell className={`h-4 w-4 ${unreadCount === 0 ? 'opacity-60' : ''}`} />
        {!mobile.isMobile && "Notifications"}
        {unreadCount > 0 && (
          <Badge variant="destructive" className="ml-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center">
            {unreadCount}
          </Badge>
        )}
      </Button>

      {/* Notifications Dropdown */}
      {showNotifications && (mobile.isMobile ? createPortal((
        <div 
          className="fixed inset-0 z-[100000] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowNotifications(false);
            }
          }}
        >
          <Card className={`${ysr2Mobile.surfaceStrong} w-full max-w-md max-h-[80dvh] overflow-hidden text-white`}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Notifications</CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={loadNotifications}
                    className="text-xs"
                    disabled={loading}
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  {unreadCount > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleMarkAllAsRead}
                        className="text-xs whitespace-nowrap"
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Mark all read
                      </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowNotifications(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-0 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">
                  Loading notifications...
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <BellOff className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                <div className={`${mobile.isMobile ? 'max-h-[60vh]' : 'max-h-96'} overflow-y-auto`}>
                  {notifications.map((notification) => (
                    <div key={notification.id} className="border-b border-white/10 last:border-b-0">
                      {renderBellNotificationRow(notification, { dark: true })}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ), document.body) : (
        <div className="absolute right-0 top-full mt-2 w-96 max-w-[90vw] z-[100000]">
          <Card className="shadow-lg border">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Notifications</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={loadNotifications} className="text-xs" disabled={loading}>
                    <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  {unreadCount > 0 && (
                    <Button size="sm" variant="outline" onClick={handleMarkAllAsRead} className="text-xs whitespace-nowrap">
                      <Check className="h-3 w-3 mr-1" />
                      Mark all read
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setShowNotifications(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading notifications...</div>
              ) : notifications.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <BellOff className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div key={notification.id} className="border-b border-gray-100 last:border-b-0">
                      {renderBellNotificationRow(notification, { dark: false })}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ))}

      {/* Backdrop to close notifications */}
      {showNotifications && !mobile.isMobile && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowNotifications(false)}
        />
      )}
    </div>
  );
};

export default UserNotificationCenter;
